import React, { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  FileUp, Loader2, Sparkles, Trash2, Upload, AlertTriangle, Camera, FileText, Scale, Activity, ScanFace,
} from "lucide-react";
import { customerApi, formatApiError } from "@/lib/api";
import {
  BIOMETRIC_BODY_FIELDS,
  BIOMETRIC_SLEEP_FIELD,
  BIOMETRIC_VO2_FIELD,
  buildBiometricPayload,
} from "@/lib/LongevityEngine";
import {
  HEALTH_BLOOD_LABELS, HEALTH_EXTRA_FIELDS, healthPayloadHasData, withHealthMetrics,
} from "./healthMetricFields";
import {
  ALL_NUMERIC_FIELDS, SEGMENTS, STATUS_OPTIONS, numberOrNull,
} from "./bodycomp/bodyCompFields";
import {
  DETECTION_ITEMS, SEVERITY_OPTIONS, SKIN_NUMERIC_FIELDS,
} from "./skin/skinFields";

const ACCEPT = "image/jpeg,image/png,image/heic,image/heif,image/webp,application/pdf,.jpg,.jpeg,.png,.heic,.heif,.pdf";
const MAX_FILES = 4;

const VAULT_ROWS = [
  ...BIOMETRIC_BODY_FIELDS.map((f) => ({ ...f, group: "body_composition" })),
  { ...BIOMETRIC_SLEEP_FIELD, group: "single" },
  { ...BIOMETRIC_VO2_FIELD, group: "single" },
  ...HEALTH_EXTRA_FIELDS.map((f) => ({ ...f, group: "single" })),
];

const emptyVaultForm = () => {
  const body = {};
  for (const f of BIOMETRIC_BODY_FIELDS) body[f.key] = "";
  return { body_composition: body, sleep_score: "", vo2_max: "", resting_heart_rate: "", daily_steps: "" };
};

const emptyBodyForm = () => {
  const values = {};
  for (const f of ALL_NUMERIC_FIELDS) values[f.key] = "";
  const seg = () => Object.fromEntries(SEGMENTS.map((s) => [s.key, { value: "", status: "" }]));
  return { test_time: "", values, segmental_fat: seg(), segmental_muscle: seg() };
};

const emptySkinForm = () => ({
  test_time: "",
  report_no: "",
  operator: "",
  skin_tone: "",
  values: { comprehensive_score: "", skin_age: "" },
  detections: Object.fromEntries(DETECTION_ITEMS.map((d) => [d.key, { percent: "", severity: "" }])),
});

const ROUTE_LABEL = {
  body_composition: { title: "Body Composition", icon: Scale, note: "HOWBODY Body Composition Analysis" },
  skin_analysis: { title: "Skin Analysis", icon: ScanFace, note: "MEICET / VISIA Skin Analysis Test Report" },
  health_app: { title: "Biometric Vault", icon: Activity, note: "Health app metrics" },
};

const SegmentEditor = ({ title, group, form, setForm, prefix }) => (
  <div className="rounded-xl border p-3">
    <p className="text-[11px] font-semibold mb-2">{title}</p>
    <div className="space-y-2">
      {SEGMENTS.map((s) => (
        <div key={s.key} className="grid grid-cols-[88px_1fr_1fr] items-center gap-2">
          <span className="text-[11px] text-muted-foreground">{s.label}</span>
          <Input
            inputMode="decimal" placeholder="kg"
            value={form[group][s.key].value}
            onChange={(e) => setForm((f) => ({
              ...f, [group]: { ...f[group], [s.key]: { ...f[group][s.key], value: e.target.value } },
            }))}
            className="h-8 text-xs"
            data-testid={`${prefix}-${s.key}-value`}
          />
          <select
            value={form[group][s.key].status}
            onChange={(e) => setForm((f) => ({
              ...f, [group]: { ...f[group], [s.key]: { ...f[group][s.key], status: e.target.value } },
            }))}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs"
            data-testid={`${prefix}-${s.key}-status`}
          >
            <option value="">—</option>
            {STATUS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      ))}
    </div>
  </div>
);

/**
 * Upload Health Report — the single upload entry point on /portal/longevity.
 *
 * upload → AI identifies the report type → (choose, if unknown) → review every
 * extracted value → save. Body composition reports append through
 * `POST /longevity/me/body-composition`; health-app metrics append through the
 * UNCHANGED vault `onSaveVault` handler. Neither path ever overwrites history.
 */
export default function HealthReportDialog({ open, onOpenChange, onSaveVault, onSavedBodyComp, onSavedSkin }) {
  const [files, setFiles] = useState([]);
  const [stage, setStage] = useState("upload"); // upload | choose | review
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [route, setRoute] = useState(null); // body_composition | health_app
  const [detected, setDetected] = useState("");
  const [notes, setNotes] = useState("");
  const [foundCount, setFoundCount] = useState(0);
  const [vaultForm, setVaultForm] = useState(emptyVaultForm);
  const [extra, setExtra] = useState({});
  const [bodyForm, setBodyForm] = useState(emptyBodyForm);
  const [skinForm, setSkinForm] = useState(emptySkinForm);
  const [skinImages, setSkinImages] = useState([]);
  const [skinTester, setSkinTester] = useState({});
  const [ranges, setRanges] = useState({});
  const [userInfo, setUserInfo] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const cameraRef = useRef(null);

  const reset = useCallback(() => {
    setFiles([]); setStage("upload"); setExtracting(false); setSaving(false);
    setRoute(null); setDetected(""); setNotes(""); setFoundCount(0);
    setVaultForm(emptyVaultForm()); setExtra({}); setBodyForm(emptyBodyForm());
    setSkinForm(emptySkinForm()); setSkinImages([]); setSkinTester({});
    setRanges({}); setUserInfo({}); setDragOver(false);
  }, []);

  const close = useCallback((next) => {
    if (!next) reset();
    onOpenChange(next);
  }, [onOpenChange, reset]);

  const addFiles = useCallback((incoming) => {
    const list = Array.from(incoming || []).filter((f) => f && f.size > 0);
    if (!list.length) return;
    setFiles((prev) => {
      if (prev.length + list.length > MAX_FILES) toast.error(`Up to ${MAX_FILES} files at a time.`);
      return [...prev, ...list].slice(0, MAX_FILES);
    });
  }, []);

  const applyPayload = useCallback((data) => {
    const bc = data.body_composition || {};
    const nextBody = emptyBodyForm();
    for (const f of ALL_NUMERIC_FIELDS) nextBody.values[f.key] = bc.values?.[f.key] ?? "";
    nextBody.test_time = bc.test_time || "";
    for (const s of SEGMENTS) {
      nextBody.segmental_fat[s.key] = {
        value: bc.segmental_fat?.[s.key]?.value ?? "",
        status: bc.segmental_fat?.[s.key]?.status ?? "",
      };
      nextBody.segmental_muscle[s.key] = {
        value: bc.segmental_muscle?.[s.key]?.value ?? "",
        status: bc.segmental_muscle?.[s.key]?.status ?? "",
      };
    }
    setBodyForm(nextBody);
    setRanges(bc.ranges || {});
    setUserInfo(bc.user_info || {});

    const sk = data.skin_analysis || {};
    const nextSkin = emptySkinForm();
    nextSkin.test_time = sk.test_time || "";
    nextSkin.report_no = sk.report_no || "";
    nextSkin.operator = sk.operator || "";
    nextSkin.skin_tone = sk.skin_tone || "";
    for (const f of SKIN_NUMERIC_FIELDS) nextSkin.values[f.key] = sk.values?.[f.key] ?? "";
    for (const d of DETECTION_ITEMS) {
      nextSkin.detections[d.key] = {
        percent: sk.detections?.[d.key]?.percent ?? "",
        severity: sk.detections?.[d.key]?.severity ?? "",
      };
    }
    setSkinForm(nextSkin);
    setSkinImages(sk.images || []);
    setSkinTester(sk.tester || {});

    const ha = data.health_app || {};
    const values = ha.values || {};
    const nextVault = emptyVaultForm();
    for (const f of BIOMETRIC_BODY_FIELDS) nextVault.body_composition[f.key] = values[f.key] ?? "";
    nextVault.sleep_score = values.sleep_score ?? "";
    nextVault.vo2_max = values.vo2_max ?? "";
    nextVault.resting_heart_rate = values.resting_heart_rate ?? "";
    nextVault.daily_steps = values.daily_steps ?? "";
    setVaultForm(nextVault);

    const extras = { ...(ha.extra_metrics || {}) };
    for (const [k, v] of Object.entries(ha.blood_results || {})) {
      if (v === null || v === undefined || v === "") continue;
      const meta = HEALTH_BLOOD_LABELS[k] || { label: k, unit: "" };
      extras[meta.label] = `${v}${meta.unit ? ` ${meta.unit}` : ""}`;
    }
    setExtra(extras);
  }, []);

  const handleExtract = useCallback(async () => {
    if (!files.length) {
      toast.error("Add your report first.");
      return;
    }
    setExtracting(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f, f.name || "report.jpg"));
      const r = await customerApi.post("/longevity/me/health-report/extract", fd);
      const data = r.data || {};
      applyPayload(data);
      setDetected(data.detected_title || "");
      setNotes(data.notes || "");
      setFoundCount((data.found || []).length);

      if (data.report_type === "unknown") {
        setRoute(null);
        setStage("choose");
        return;
      }
      setRoute(data.report_type);
      setStage("review");
      const where = ROUTE_LABEL[data.report_type]?.title || "your vault";
      toast.success(`Read ${(data.found || []).length} values · routed to ${where}.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExtracting(false);
    }
  }, [files, applyPayload]);

  const chooseRoute = (kind) => { setRoute(kind); setStage("review"); };

  const setVaultRow = (row, value) => {
    if (row.group === "body_composition") {
      setVaultForm((f) => ({ ...f, body_composition: { ...f.body_composition, [row.key]: value } }));
    } else {
      setVaultForm((f) => ({ ...f, [row.key]: value }));
    }
  };
  const vaultRowValue = (row) =>
    row.group === "body_composition" ? vaultForm.body_composition[row.key] ?? "" : vaultForm[row.key] ?? "";

  const vaultPayload = useMemo(
    () => withHealthMetrics(buildBiometricPayload(vaultForm, { includeBlood: false }), {
      ...vaultForm, extra_metrics: extra, source: "health_report_upload",
    }),
    [vaultForm, extra],
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      if (route === "health_app") {        if (!healthPayloadHasData(vaultPayload)) {
          toast.error("Add at least one value before saving.");
          return;
        }
        await onSaveVault(vaultPayload);
        toast.success("New biometric record saved to your vault.");
      } else if (route === "skin_analysis") {
        const score = numberOrNull(skinForm.values.comprehensive_score);
        const skinAge = numberOrNull(skinForm.values.skin_age);
        const detections = Object.fromEntries(DETECTION_ITEMS.map((d) => [d.key, {
          percent: numberOrNull(skinForm.detections[d.key].percent),
          severity: skinForm.detections[d.key].severity || null,
        }]));
        const tone = (skinForm.skin_tone || "").trim();
        if (score === null && skinAge === null && !tone
            && !Object.values(detections).some((d) => d.percent !== null)) {
          toast.error("Add at least one value before saving.");
          return;
        }
        await customerApi.post("/longevity/me/skin-analysis", {
          test_time: skinForm.test_time || null,
          comprehensive_score: score,
          skin_age: skinAge,
          skin_tone: tone || null,
          report_no: skinForm.report_no || null,
          operator: skinForm.operator || null,
          detections,
          tester: skinTester,
          images: skinImages,
          notes,
          source: "meicet_upload",
        });
        toast.success("Skin analysis report saved.");
        window.dispatchEvent(new CustomEvent("cocoon:skin-analysis-saved"));
        await onSavedSkin?.();
      } else {
        const values = {};
        for (const f of ALL_NUMERIC_FIELDS) {
          const n = numberOrNull(bodyForm.values[f.key]);
          if (n !== null) values[f.key] = n;
        }
        const segOut = (group) => Object.fromEntries(SEGMENTS.map((s) => [s.key, {
          value: numberOrNull(bodyForm[group][s.key].value),
          status: bodyForm[group][s.key].status || null,
        }]));
        if (!Object.keys(values).length
            && !Object.values(segOut("segmental_fat")).some((s) => s.value !== null)
            && !Object.values(segOut("segmental_muscle")).some((s) => s.value !== null)) {
          toast.error("Add at least one value before saving.");
          return;
        }
        await customerApi.post("/longevity/me/body-composition", {
          test_time: bodyForm.test_time || null,
          comprehensive_score: values.comprehensive_score ?? null,
          values,
          segmental_fat: segOut("segmental_fat"),
          segmental_muscle: segOut("segmental_muscle"),
          ranges,
          user_info: userInfo,
          notes,
          source: "health_report_upload",
        });
        toast.success("Body composition report saved.");
        window.dispatchEvent(new CustomEvent("cocoon:body-composition-saved"));
        await onSavedBodyComp?.();
      }
      close(false);
    } catch (e) {
      toast.error(formatApiError(e) || e?.message || "Could not save your entry.");
    } finally {
      setSaving(false);
    }
  }, [route, vaultPayload, onSaveVault, bodyForm, ranges, userInfo, notes, onSavedBodyComp,
      skinForm, skinImages, skinTester, onSavedSkin, close]);

  const RouteIcon = route ? ROUTE_LABEL[route].icon : Sparkles;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="health-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Sparkles className="w-4 h-4 text-primary" /> Upload Health Report
          </DialogTitle>
          <DialogDescription className="text-xs">
            {stage === "upload"
              ? "Any health report — a HOWBODY body composition sheet, a MEICET / VISIA skin analysis report, an Apple Health / Google Health / Fitbit screenshot or a lab result. We'll recognise it and read the values for you."
              : stage === "choose"
                ? "We couldn't tell what this report is. Choose the section it belongs to."
                : "Check each value and correct anything we misread before confirming."}
          </DialogDescription>
        </DialogHeader>

        {stage === "upload" && (
          <div className="space-y-3">
            <div
              role="button"
              tabIndex={0}
              onClick={() => inputRef.current?.click()}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") inputRef.current?.click(); }}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer?.files); }}
              className={`rounded-2xl border-2 border-dashed p-7 text-center cursor-pointer transition-colors ${
                dragOver ? "border-primary bg-primary/10" : "border-primary/30 bg-primary/5 hover:bg-primary/10"
              }`}
              data-testid="health-report-dropzone"
            >
              <FileUp className="w-7 h-7 text-primary mx-auto mb-2" />
              <p className="text-sm font-medium">Tap to choose your report</p>
              <p className="text-[11px] text-muted-foreground mt-1">
                or drag it here · JPG, PNG, HEIC or PDF · up to {MAX_FILES} files
              </p>
            </div>

            <div className="flex justify-center sm:hidden">
              <Button
                type="button" size="sm" variant="ghost" className="rounded-full text-xs"
                onClick={() => cameraRef.current?.click()}
                data-testid="health-report-camera-btn"
              >
                <Camera className="w-3.5 h-3.5 mr-1.5" /> Take a photo instead
              </Button>
            </div>

            <input
              ref={inputRef} type="file" accept={ACCEPT} multiple className="hidden"
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
              data-testid="health-report-file-input"
            />
            <input
              ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
              data-testid="health-report-camera-input"
            />

            {files.length > 0 && (
              <div className="space-y-1.5" data-testid="health-report-file-list">
                {files.map((f, i) => (
                  <div key={`${f.name}-${i}`} className="flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2">
                    <FileText className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                    <span className="text-xs truncate flex-1">{f.name}</span>
                    <button
                      type="button"
                      onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                      aria-label={`Remove ${f.name}`}
                      data-testid={`health-report-remove-${i}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-1">
              <Button variant="ghost" className="rounded-full" onClick={() => close(false)} data-testid="health-report-cancel-btn">
                Cancel
              </Button>
              <Button
                className="rounded-full" onClick={handleExtract}
                disabled={extracting || files.length === 0}
                data-testid="health-report-extract-btn"
              >
                {extracting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                {extracting ? "Reading your report…" : "Read my report"}
              </Button>
            </div>
          </div>
        )}

        {stage === "choose" && (
          <div className="space-y-3" data-testid="health-report-choose">
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-900" data-testid="health-report-unknown-note">
                {notes || "We couldn't tell which kind of health report this is."}
                {detected ? ` We read the title as "${detected}".` : ""}
              </p>
            </div>
            <p className="text-xs text-muted-foreground">Which section does this upload belong to?</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.entries(ROUTE_LABEL).map(([kind, meta]) => {
                const Icon = meta.icon;
                return (
                  <button
                    key={kind}
                    type="button"
                    onClick={() => chooseRoute(kind)}
                    className="rounded-xl border p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-md"
                    data-testid={`health-report-choose-${kind}`}
                  >
                    <Icon className="w-5 h-5 text-primary mb-2" />
                    <p className="text-sm font-medium">{meta.title}</p>
                    <p className="text-[11px] text-muted-foreground">{meta.note}</p>
                  </button>
                );
              })}
            </div>
            <div className="flex justify-start pt-1">
              <Button variant="ghost" className="rounded-full text-xs" onClick={() => setStage("upload")} data-testid="health-report-choose-back">
                Upload a different file
              </Button>
            </div>
          </div>
        )}

        {stage === "review" && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="rounded-full text-[10px]" data-testid="health-report-route-badge">
                <RouteIcon className="w-3 h-3 mr-1" /> {ROUTE_LABEL[route]?.title}
              </Badge>
              <Badge variant="outline" className="rounded-full text-[10px]" data-testid="health-report-found-count">
                {foundCount} value{foundCount === 1 ? "" : "s"} read
              </Badge>
              {detected && (
                <span className="text-[11px] text-muted-foreground" data-testid="health-report-detected-title">
                  Detected: {detected}
                </span>
              )}
            </div>
            {notes && <p className="text-[11px] text-muted-foreground italic" data-testid="health-report-notes">{notes}</p>}

            {route === "health_app" ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {VAULT_ROWS.map((row) => (
                    <div key={row.key}>
                      <Label className="text-[11px] text-muted-foreground">
                        {row.label}{row.unit ? ` (${row.unit})` : ""}
                      </Label>
                      <Input
                        inputMode="decimal" placeholder="—"
                        value={vaultRowValue(row)}
                        onChange={(e) => setVaultRow(row, e.target.value)}
                        className="mt-1"
                        data-testid={`health-report-review-${row.key}`}
                      />
                    </div>
                  ))}
                </div>
                {Object.keys(extra).length > 0 && (
                  <div className="rounded-xl border bg-muted/40 p-3" data-testid="health-report-extra">
                    <p className="text-[11px] font-medium mb-1.5">Also detected — saved with your record</p>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(extra).map(([k, v]) => (
                        <Badge key={k} variant="secondary" className="rounded-full text-[10px] font-normal">{k}: {v}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : route === "skin_analysis" ? (
              <div className="space-y-3" data-testid="health-report-review-skin">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-[11px] text-muted-foreground">Test date and time</Label>
                    <Input
                      placeholder="YYYY-MM-DD HH:MM:SS"
                      value={skinForm.test_time}
                      onChange={(e) => setSkinForm((f) => ({ ...f, test_time: e.target.value }))}
                      className="mt-1"
                      data-testid="health-report-review-skin-test_time"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] text-muted-foreground">Report No.</Label>
                    <Input
                      placeholder="—"
                      value={skinForm.report_no}
                      onChange={(e) => setSkinForm((f) => ({ ...f, report_no: e.target.value }))}
                      className="mt-1"
                      data-testid="health-report-review-skin-report_no"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] text-muted-foreground">Skin Tone</Label>
                    <Input
                      placeholder="—"
                      value={skinForm.skin_tone}
                      onChange={(e) => setSkinForm((f) => ({ ...f, skin_tone: e.target.value }))}
                      className="mt-1"
                      data-testid="health-report-review-skin_tone"
                    />
                  </div>
                  {SKIN_NUMERIC_FIELDS.map((f) => (
                    <div key={f.key}>
                      <Label className="text-[11px] text-muted-foreground">
                        {f.label}{f.unit ? ` (${f.unit})` : ""}
                      </Label>
                      <Input
                        inputMode="decimal" placeholder="—"
                        value={skinForm.values[f.key]}
                        onChange={(e) => setSkinForm((prev) => ({ ...prev, values: { ...prev.values, [f.key]: e.target.value } }))}
                        className="mt-1"
                        data-testid={`health-report-review-${f.key}`}
                      />
                    </div>
                  ))}
                </div>

                <div className="rounded-xl border p-3">
                  <p className="text-[11px] font-semibold mb-2">Detection items — severity compared to peers</p>
                  <div className="space-y-2">
                    {DETECTION_ITEMS.map((d) => (
                      <div key={d.key} className="grid grid-cols-[96px_1fr_1fr] items-center gap-2">
                        <span className="text-[11px] text-muted-foreground">{d.label}</span>
                        <Input
                          inputMode="decimal" placeholder="%"
                          value={skinForm.detections[d.key].percent}
                          onChange={(e) => setSkinForm((f) => ({
                            ...f,
                            detections: { ...f.detections, [d.key]: { ...f.detections[d.key], percent: e.target.value } },
                          }))}
                          className="h-8 text-xs"
                          data-testid={`health-report-review-detection-${d.key}-percent`}
                        />
                        <select
                          value={skinForm.detections[d.key].severity}
                          onChange={(e) => setSkinForm((f) => ({
                            ...f,
                            detections: { ...f.detections, [d.key]: { ...f.detections[d.key], severity: e.target.value } },
                          }))}
                          className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                          data-testid={`health-report-review-detection-${d.key}-severity`}
                        >
                          <option value="">—</option>
                          {SEVERITY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>

                {skinImages.length > 0 && (
                  <div className="rounded-xl border bg-muted/40 p-3" data-testid="health-report-skin-images">
                    <p className="text-[11px] font-medium mb-1.5">
                      {skinImages.length} capture page{skinImages.length === 1 ? "" : "s"} saved with this report
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      {skinImages.map((img, i) => (
                        <img
                          key={img.path || i}
                          src={img.url}
                          alt={`Report page ${i + 1}`}
                          className="h-16 w-auto rounded-md border"
                          data-testid={`health-report-skin-image-${i}`}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div>
                  <Label className="text-[11px] text-muted-foreground">Test date and time</Label>
                  <Input
                    placeholder="YYYY-MM-DD HH:MM:SS"
                    value={bodyForm.test_time}
                    onChange={(e) => setBodyForm((f) => ({ ...f, test_time: e.target.value }))}
                    className="mt-1"
                    data-testid="health-report-review-test_time"
                  />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {ALL_NUMERIC_FIELDS.map((f) => (
                    <div key={f.key}>
                      <Label className="text-[11px] text-muted-foreground">
                        {f.label}{f.unit ? ` (${f.unit})` : ""}
                      </Label>
                      <Input
                        inputMode="decimal" placeholder="—"
                        value={bodyForm.values[f.key]}
                        onChange={(e) => setBodyForm((prev) => ({ ...prev, values: { ...prev.values, [f.key]: e.target.value } }))}
                        className="mt-1"
                        data-testid={`health-report-review-${f.key}`}
                      />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <SegmentEditor title="Segmental Fat (kg)" group="segmental_fat" form={bodyForm} setForm={setBodyForm} prefix="health-report-review-fat" />
                  <SegmentEditor title="Segmental Muscle (kg)" group="segmental_muscle" form={bodyForm} setForm={setBodyForm} prefix="health-report-review-muscle" />
                </div>
              </>
            )}

            <div className="flex items-center justify-between gap-2 pt-1">
              <Button variant="ghost" className="rounded-full text-xs" onClick={() => setStage("upload")} data-testid="health-report-back-btn">
                Upload a different report
              </Button>
              <Button className="rounded-full" onClick={handleSave} disabled={saving} data-testid="health-report-confirm-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                {saving ? "Saving…" : "Confirm and Save"}
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Saving adds a new timestamped record — none of your earlier entries are changed.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
